import { Classes, Colors, FileInput, Intent } from "@blueprintjs/core";
import { getIn } from "formik";
import {
  FGCustomInput,
  FieldGroup,
  FlexFieldGroup,
  IFGCustomInputProps,
  showError,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { DownloadButton, MessageBubble } from "..";
import { DocumentDto, FileResponse } from "../../api";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import { downloadFileResponse } from "../../utils/downloadFileResponse";

const StyledFileInput = styled(FileInput)`
  & .${Classes.FILE_UPLOAD_INPUT} {
    color: ${Colors.BLACK} !important;
  }
`;

//This takes in a DocumentDto
export interface FGFileInputProps
  extends Omit<IFGCustomInputProps, "children"> {
  downloadFn?: () => Promise<FileResponse>;
  validationErrors?: any;
  validationErrorsKey?: string;
}

export const FGFileInput: React.FunctionComponent<FGFileInputProps> = ({
  name,
  downloadFn = null,
  validationErrors,
  validationErrorsKey,
  ...customInputProps
}) => {
  const { t } = useCommonHooks();
  const { formik, editMode } = useFGContext();

  const [downloading, setdownloading] = React.useState(false);

  const file = React.useMemo(() => {
    const data = getIn(formik?.values, name);
    if (!data) return null;
    if (!(data instanceof DocumentDto))
      throw new Error(
        `The value of FGFileInput must be an object containing a property named "fileName" or containing "FileName" (Field: ${name})`
      );
    return data;
  }, [formik?.values, name]);

  const downloadDocument = React.useCallback(async () => {
    try {
      setdownloading(true);
      const file = await downloadFn();
      await downloadFileResponse(file);
    } catch (err: any) {
      console.log("Error: ", err);
      showError(t(ETLCodes.Error));
    }
    setdownloading(false);
  }, [downloadFn, t]);

  const onChange = React.useCallback(
    (e) => {
      if (e.target.files.length > 0) {
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          const filePathSplit = e.target.value.split("\\");

          const newFile = new DocumentDto({
            ...getIn(formik?.values, name),
            fileName: filePathSplit[filePathSplit.length - 1],
            contentAsBase64: reader.result.toString(),
          });

          formik.setFieldTouched(`${name}.fileName`, true);
          formik.setFieldValue(name, newFile, true);
        });
        reader.readAsDataURL(e.target.files[0]);
      } else {
        formik.setFieldTouched(`${name}.fileName`, true);
        formik.setFieldValue(name, null, true);
      }
    },
    [formik, name]
  );

  return (
    <FGCustomInput name={`${name}.fileName`} {...customInputProps}>
      {() => (
        <>
          {((validationErrors && validationErrors[validationErrorsKey]) ||
            formik.errors?.[name]) && (
            <FlexFieldGroup>
              <MessageBubble
                intent={Intent.DANGER}
                innerMessage={t(ETLCodes.Required)}
              />
            </FlexFieldGroup>
          )}
          <FieldGroup columns={[11, 1]}>
            <StyledFileInput
              text={file?.fileName ?? t(ETLCodes.ChooseAFile)}
              onInputChange={onChange}
              buttonText={t(ETLCodes.Browse)}
              fill={true}
              disabled={!editMode}
            />
            {downloadFn && file !== null && (
              <DownloadButton
                onClick={downloadDocument}
                loading={downloading}
              />
            )}
          </FieldGroup>
        </>
      )}
    </FGCustomInput>
  );
};
